import { Button } from '@amore/ui/components/button'
import { Checkbox } from '@amore/ui/components/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@amore/ui/components/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import type { ReactNode } from 'react'

export type ItemMenuEntry = { label: string; onSelect: () => void; tone?: 'danger' }

/**
 * One repeatable, editable item inside a form: a person in an invitation, a
 * payment, a part of the day. Header: index, title, "⋯" menu. Body: fields in
 * `ItemCardFields`. Never a loud red button in the header.
 */
export function ItemCard({
  index,
  title,
  fallbackTitle,
  meta,
  menu,
  children,
}: {
  index?: number
  title: string
  /** Shown when the title is empty, e.g. "Doprovod". */
  fallbackTitle: string
  meta?: ReactNode
  /** Hidden for viewers: pass an empty list. */
  menu: ItemMenuEntry[]
  children: ReactNode
}) {
  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        {index !== undefined ? (
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
            {index}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {title.trim() || <span className="text-muted-foreground">{fallbackTitle}</span>}
          </p>
          {meta ? <p className="truncate text-xs text-muted-foreground">{meta}</p> : null}
        </div>
        {menu.length > 0 ? <ItemMenu entries={menu} /> : null}
      </header>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  )
}

/** A "⋯" menu: the place for "Odebrat", "Duplikovat" and similar item actions. */
export function ItemMenu({
  entries,
  label = 'Další akce',
}: {
  entries: ItemMenuEntry[]
  label?: string
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button appearance="ghost" tone="neutral" size="icon" aria-label={label} className="size-9">
          <MoreHorizontal className="size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {entries.map((entry) => (
          <DropdownMenuItem
            key={entry.label}
            onSelect={entry.onSelect}
            className={entry.tone === 'danger' ? 'text-danger-foreground' : undefined}
          >
            {entry.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The field grid inside an item: two equal columns, one on mobile, aligned to the top so a hint under one field doesn't push its neighbour down. */
export function ItemCardFields({ children }: { children: ReactNode }) {
  return <div className="grid items-start gap-4 sm:grid-cols-2">{children}</div>
}

/** A labelled group of checkboxes laid out as an aligned grid (e.g. diets). */
export function CheckboxGrid<V extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string
  options: { value: V; label: string }[]
  value: V[]
  onChange: (next: V[]) => void
  disabled?: boolean
}) {
  return (
    <fieldset className="space-y-1 sm:col-span-2">
      <legend className="text-xs font-semibold">{label}</legend>
      <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-4">
        {options.map((option) => (
          <Checkbox
            key={option.value}
            checked={value.includes(option.value)}
            disabled={disabled}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...value, option.value]
                  : value.filter((item) => item !== option.value),
              )
            }
          >
            {option.label}
          </Checkbox>
        ))}
      </div>
    </fieldset>
  )
}

/** The add actions under a list of items: one row of secondary buttons. */
export function ItemAddRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2">{children}</div>
}
